/**
 * ISO 8601 week helpers for the task board's week selector.
 *
 * Weeks start on Monday and are anchored on their Thursday (so the ISO
 * week-year can differ from the calendar year in late December / early
 * January). All "current week" math is resolved in Helsinki local time to
 * match the backend's `_apply_deadline_week_filter`.
 *
 * Do NOT replace this with the naive `Math.ceil((t - yearStart) / 7)` used in
 * LBSChart.tsx — that doesn't follow ISO 8601 and breaks around year
 * boundaries.
 */

export type IsoWeek = { year: number; week: number };

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * ISO 8601 week of the given date, using the date's *calendar* Y/M/D
 * (timezone-agnostic — the caller decides which calendar date to pass).
 */
export function getIsoWeek(date: Date): IsoWeek {
  // Work in UTC to avoid DST hour shifts affecting the day arithmetic.
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  // Day number with Monday = 0 … Sunday = 6.
  const dayNum = (d.getUTCDay() + 6) % 7;
  // Shift to the Thursday of this week; its calendar year is the ISO year.
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const isoYear = d.getUTCFullYear();
  // Thursday of ISO week 1 is the Thursday in the week containing Jan 4.
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstThursdayDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(
    firstThursday.getUTCDate() - firstThursdayDayNum + 3,
  );
  const week =
    1 + Math.round((d.getTime() - firstThursday.getTime()) / MS_PER_WEEK);
  return { year: isoYear, week };
}

/** The Helsinki-local calendar Y/M/D of the given instant. */
function helsinkiYMD(date: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Helsinki",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

/** Current ISO week, resolved in Helsinki local time. */
export function getCurrentIsoWeek(): IsoWeek {
  const { year, month, day } = helsinkiYMD(new Date());
  return getIsoWeek(new Date(year, month - 1, day));
}

export function isoWeekEquals(a: IsoWeek, b: IsoWeek): boolean {
  return a.year === b.year && a.week === b.week;
}

/** Chronological comparison: -1 if a < b, 0 if equal, 1 if a > b. */
export function compareIsoWeeks(a: IsoWeek, b: IsoWeek): number {
  if (a.year !== b.year) return a.year < b.year ? -1 : 1;
  if (a.week !== b.week) return a.week < b.week ? -1 : 1;
  return 0;
}

/**
 * Whole-week offset of `week` relative to `from` (e.g. +2, 0, -1). Computed
 * from the weeks' Mondays so it stays correct across ISO year boundaries,
 * where plain week-number subtraction breaks.
 */
export function isoWeekOffset(week: IsoWeek, from: IsoWeek): number {
  const ms = isoWeekMonday(week).getTime() - isoWeekMonday(from).getTime();
  return Math.round(ms / MS_PER_WEEK);
}

/**
 * The Monday (00:00) that starts the given ISO week, returned as a Date whose
 * UTC Y/M/D is that Monday's calendar date. Use the UTC getters for display.
 */
function isoWeekMonday(w: IsoWeek): Date {
  // Jan 4 is always in ISO week 1.
  const jan4 = new Date(Date.UTC(w.year, 0, 4));
  const jan4DayNum = (jan4.getUTCDay() + 6) % 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4DayNum);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (w.week - 1) * 7);
  return monday;
}

/**
 * Helsinki-local half-open interval [Monday 00:00, next Monday 00:00) of the
 * week. Provided for completeness; the board filters server-side by week
 * number, so this is not on the query path.
 */
export function isoWeekToDateRange(w: IsoWeek): {
  start: Date;
  endExclusive: Date;
} {
  const monday = isoWeekMonday(w);
  const start = new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate());
  const endExclusive = new Date(start);
  endExclusive.setDate(start.getDate() + 7);
  return { start, endExclusive };
}

/** Short chip label, e.g. "vk 23". */
export function formatWeekChipLabel(w: IsoWeek): string {
  return `vk ${w.week}`;
}

/**
 * Short label for the week's Monday with a trailing period, e.g. "1.6."
 * (the Monday that starts the ISO week).
 */
export function formatWeekMonday(w: IsoWeek): string {
  const monday = isoWeekMonday(w);
  const day = monday.getUTCDate();
  const month = monday.getUTCMonth() + 1;
  return `${day}.${month}.`;
}
